import { createContext, useContext, useState, useEffect } from 'react'
import { useTelegram } from './TelegramContext'
import { getCart as getCartAPI, addToCart as addToCartAPI, removeFromCart as removeFromCartAPI, clearCart as clearCartAPI } from '../api'

const CartContext = createContext({})

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([])
  const [loading, setLoading] = useState(true)
  const { hapticFeedback, user } = useTelegram()

  // Загружаем корзину из localStorage и синхронизируем с сервером при монтировании
  useEffect(() => {
    if (user?.id) {
      loadCart()
    } else {
      setLoading(false)
    }
  }, [user?.id])

  // Функция загрузки корзины с сервера
  const loadCart = async () => {
    try {
      setLoading(true)
      // Сначала пытаемся загрузить с сервера
      const serverCart = await getCartAPI(user.id)
      
      // Проверяем локальную корзину
      const localCart = getLocalCart()
      
      // Если локальная корзина есть и отличается, мерджим
      if (localCart && localCart.length > 0) {
        // Синхронизируем локальную корзину с сервером
        const mergedCart = mergeCartItems(serverCart, localCart)
        setCart(mergedCart)
        // Очищаем локальную корзину после синхронизации
        clearLocalCart()
      } else {
        setCart(serverCart)
      }
    } catch (error) {
      console.error('Ошибка загрузки корзины с сервера:', error)
      // Если сервер недоступен, загружаем из localStorage
      const localCart = getLocalCart()
      if (localCart) {
        setCart(localCart)
      }
    } finally {
      setLoading(false)
    }
  }

  // Получить корзину из localStorage с привязкой к user_id
  const getLocalCart = () => {
    if (!user?.id) return []
    
    const key = `cart_${user.id}`
    const savedCart = localStorage.getItem(key)
    
    if (savedCart) {
      try {
        return JSON.parse(savedCart)
      } catch (e) {
        console.error('Ошибка парсинга корзины:', e)
        return []
      }
    }
    return []
  }

  // Сохранить корзину в localStorage с привязкой к user_id
  const saveLocalCart = (cartData) => {
    if (!user?.id) return
    
    const key = `cart_${user.id}`
    localStorage.setItem(key, JSON.stringify(cartData))
  }

  // Очистить локальную корзину
  const clearLocalCart = () => {
    if (!user?.id) return
    
    const key = `cart_${user.id}`
    localStorage.removeItem(key)
  }

  // Объединить корзины (сервер + локальная)
  const mergeCartItems = (serverCart, localCart) => {
    const merged = [...serverCart]
    
    localCart.forEach(localItem => {
      const existingIndex = merged.findIndex(
        item => 
          item.product_id === localItem.product_id &&
          item.size === localItem.size &&
          item.color === localItem.color
      )
      
      if (existingIndex > -1) {
        // Увеличиваем количество
        merged[existingIndex].quantity += localItem.quantity
      } else {
        // Добавляем новый товар
        merged.push(localItem)
      }
    })
    
    return merged
  }

  // Сохраняем корзину в localStorage при изменении
  useEffect(() => {
    if (!loading && cart.length >= 0) {
      saveLocalCart(cart)
    }
  }, [cart, loading])

  const addToCart = async (product, quantity = 1, size = null, color = null) => {
    // Обновляем локально
    setCart(prevCart => {
      // Проверяем, есть ли такой товар с такими параметрами
      const existingItemIndex = prevCart.findIndex(
        item => 
          item.product_id === product.id &&
          item.size === size &&
          item.color === color
      )

      let newCart
      if (existingItemIndex > -1) {
        // Увеличиваем количество
        newCart = [...prevCart]
        newCart[existingItemIndex].quantity += quantity
      } else {
        // Добавляем новый товар
        newCart = [...prevCart, {
          product_id: product.id,
          product: product,
          quantity,
          size,
          color
        }]
      }

      // Синхронизируем с сервером (не блокируем UI)
      if (user?.id) {
        addToCartAPI(user.id, {
          product_id: product.id,
          quantity,
          size,
          color
        }).catch(error => {
          console.error('Ошибка добавления в корзину на сервере:', error)
        })
      }

      return newCart
    })
    
    hapticFeedback?.('success')
  }

  const removeFromCart = async (productId, size = null, color = null) => {
    setCart(prevCart => prevCart.filter(
      item => !(
        item.product_id === productId &&
        item.size === size &&
        item.color === color
      )
    ))

    // Синхронизируем с сервером
    if (user?.id) {
      removeFromCartAPI(user.id, productId, size, color).catch(error => {
        console.error('Ошибка удаления из корзины на сервере:', error)
      })
    }
    
    hapticFeedback?.('light')
  }

  const updateQuantity = async (productId, size, color, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId, size, color)
      return
    }

    setCart(prevCart => prevCart.map(item => {
      if (
        item.product_id === productId &&
        item.size === size &&
        item.color === color
      ) {
        return { ...item, quantity: newQuantity }
      }
      return item
    }))

    // Синхронизируем с сервером (обновление = удаление + добавление)
    if (user?.id) {
      try {
        await removeFromCartAPI(user.id, productId, size, color)
        await addToCartAPI(user.id, {
          product_id: productId,
          quantity: newQuantity,
          size,
          color
        })
      } catch (error) {
        console.error('Ошибка обновления корзины на сервере:', error)
      }
    }
  }

  const clearCart = async () => {
    setCart([])

    // Синхронизируем с сервером
    if (user?.id) {
      clearCartAPI(user.id).catch(error => {
        console.error('Ошибка очистки корзины на сервере:', error)
      })
    }
    
    hapticFeedback?.('light')
  }

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.product.price * item.quantity), 0)
  }

  const getCartItemsCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0)
  }

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    hapticFeedback,
    reloadCart: loadCart
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => useContext(CartContext)












